import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandMark, EventismCredit } from "@/components/Brand";
import { toast } from "sonner";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const auth = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await auth.register(name, email, password, phone);
      toast.success("Account created. Welcome!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-slate flex flex-col">
      <div className="px-6 py-5 flex justify-between items-center border-b border-slate-100 bg-white">
        <BrandMark />
        <Link
          to="/"
          className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-brand-red"
          data-testid="register-back-home-link"
        >
          <i className="fas fa-arrow-left mr-2" /> Back
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-[32px] shadow-xl border border-slate-100 overflow-hidden">
          <div className="bg-brand-blue text-white px-8 py-7">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-2">
              <i className="fas fa-user-plus mr-2" /> Get Started
            </p>
            <h1 className="text-3xl font-black uppercase tracking-tightest">Open the Pod</h1>
            <p className="text-white/70 text-xs font-bold mt-1">
              30 seconds. Then book gear at the Holly St. Pod.
            </p>
          </div>
          <form onSubmit={submit} className="px-8 py-7 space-y-4" data-testid="register-form">
            <Field
              id="r-name"
              label="Full Name"
              type="text"
              value={name}
              onChange={setName}
              testId="register-name-input"
              required
            />
            <Field
              id="r-email"
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              testId="register-email-input"
              required
            />
            <Field
              id="r-phone"
              label="Mobile Number — required for PIN delivery"
              type="tel"
              value={phone}
              onChange={setPhone}
              testId="register-phone-input"
              required
              placeholder="+1 720 555 1212"
            />
            <Field
              id="r-password"
              label="Password (min 6 chars)"
              type="password"
              value={password}
              onChange={setPassword}
              testId="register-password-input"
              required
            />
            {error && (
              <div
                className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-bold text-brand-red"
                data-testid="register-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
              data-testid="register-submit-button"
            >
              {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Creating</> : <>Create Account</>}
            </button>
            <p className="text-center text-xs font-bold text-slate-500 mt-2">
              Already have one?{" "}
              <Link to="/login" className="text-brand-red underline" data-testid="register-login-link">
                Log in
              </Link>
            </p>
          </form>
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-center">
            <EventismCredit />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, type, value, onChange, required, testId, placeholder }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[10px] font-black uppercase tracking-widest text-brand-blue mb-1.5"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10"
        data-testid={testId}
      />
    </div>
  );
}
